import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import {
  AlertTriangle, Ban, Flag, Shield, FileText
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const FraudTab = ({ headers }) => {
  const [suspicious, setSuspicious] = useState(null);
  const [referralAbuse, setReferralAbuse] = useState(null);
  const [defaulters, setDefaulters] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true);
      try {
        const [s, r, d, a] = await Promise.all([
          axios.get(`${API_URL}/api/admin/fraud/suspicious-accounts`, { headers }),
          axios.get(`${API_URL}/api/admin/fraud/referral-abuse`, { headers }),
          axios.get(`${API_URL}/api/admin/defaulters`, { headers }),
          axios.get(`${API_URL}/api/admin/audit-logs?limit=20`, { headers }),
        ]);
        setSuspicious(s.data);
        setReferralAbuse(r.data);
        setDefaulters(d.data);
        setAuditLogs(a.data);
      } catch { toast.error('Failed'); }
      finally { setLoading(false); }
    };
    fetchAll();
  }, []);

  if (loading) return <div className="space-y-3">{[1,2,3].map(i => <Skeleton key={i} className="h-28 rounded-xl"/>)}</div>;

  const suspTotal = suspicious ? suspicious.total : 0;
  const suspAccounts = suspicious ? suspicious.accounts : [];
  const refReferrers = referralAbuse ? referralAbuse.referrers : [];

  return (
    <div className="space-y-4">
      <Card className="glass-card">
        <CardHeader className="pb-2"><CardTitle className="text-lg text-[#0B1F3A] flex items-center gap-2"><AlertTriangle size={18} className="text-red-500"/>Suspicious Accounts ({suspTotal})</CardTitle></CardHeader>
        <CardContent>
          {suspAccounts.length === 0 ? <p className="text-center py-4 text-emerald-600 font-medium">No suspicious activity</p> :
          <div className="space-y-2">{suspAccounts.map((a, i) => {
            const severity = a.severity;
            const aType = a.type;
            const bgClass = severity === 'high' ? 'bg-red-50 border border-red-200' : 'bg-amber-50 border border-amber-200';
            const iconBg = severity === 'high' ? 'bg-red-100' : 'bg-amber-100';
            return (
              <div key={i} className={`p-3 rounded-xl flex items-center gap-3 ${bgClass}`} data-testid={`suspicious-${i}`}>
                <div className={`p-2 rounded-lg ${iconBg}`}>
                  {aType === 'flagged' ? <Flag size={14} className="text-red-600"/> : <AlertTriangle size={14} className="text-amber-600"/>}
                </div>
                <div className="flex-1">
                  <p className="text-sm font-bold text-[#0B1F3A]">{a.user_name}</p>
                  <p className="text-xs text-slate-500">{a.email}</p>
                  <p className="text-xs text-red-600 mt-0.5">{a.reason}</p>
                </div>
                <Badge className={severity === 'high' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}>{aType.replace('_',' ')}</Badge>
              </div>
            );
          })}</div>}
        </CardContent>
      </Card>

      <Card className="glass-card">
        <CardHeader className="pb-2"><CardTitle className="text-lg text-[#0B1F3A] flex items-center gap-2"><Ban size={18} className="text-red-500"/>Defaulters ({defaulters.length})</CardTitle></CardHeader>
        <CardContent>
          {defaulters.length === 0 ? <p className="text-center py-4 text-emerald-600 font-medium">No defaulters</p> :
          <div className="space-y-2">{defaulters.map((d, i) => {
            const dName = d.user_name;
            const dEmail = d.user_email;
            const dScore = d.credibility_score;
            const dGroup = d.group_name;
            const dReason = d.default_reason;
            const dPos = d.position;
            const dContribs = d.contributions_made;
            const dDebt = d.outstanding_debt;
            return (
              <div key={i} className="p-3 rounded-xl bg-red-50 border border-red-200" data-testid={`defaulter-${i}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-bold text-[#0B1F3A]">{dName}</p>
                    <p className="text-xs text-slate-500">{dEmail} &middot; Score: {dScore}</p>
                  </div>
                  <Badge className="bg-red-100 text-red-700">{dGroup}</Badge>
                </div>
                <p className="text-xs text-red-600 mt-1">{dReason}</p>
                <div className="flex gap-4 text-xs text-slate-600 mt-1">
                  <span>Position: {dPos}</span>
                  <span>Contributed: {dContribs}</span>
                  <span>Debt: {'\u20a6'}{new Intl.NumberFormat('en-NG').format(dDebt)}</span>
                </div>
              </div>
            );
          })}</div>}
        </CardContent>
      </Card>

      {refReferrers.length > 0 && (
        <Card className="glass-card">
          <CardHeader className="pb-2"><CardTitle className="text-lg text-[#0B1F3A] flex items-center gap-2"><Shield size={18} className="text-amber-500"/>Referral Monitoring</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-2">{refReferrers.map((r, i) => {
              const rName = r.referrer_name;
              const rTotal = r.total_referrals;
              const rActive = r.active_referrals;
              const rInactive = r.inactive_referrals;
              const rRisk = r.abuse_risk;
              const bgClass = rRisk === 'high' ? 'bg-red-50 border border-red-200' : 'bg-slate-50 border border-slate-200';
              return (
                <div key={i} className={`p-3 rounded-xl ${bgClass}`}>
                  <p className="text-sm font-bold text-[#0B1F3A]">{rName}</p>
                  <div className="flex gap-4 text-xs text-slate-600 mt-1">
                    <span>Total: {rTotal}</span>
                    <span>Active: {rActive}</span>
                    <span>Inactive: {rInactive}</span>
                    <Badge className={rRisk === 'high' ? 'bg-red-100 text-red-700' : 'bg-emerald-100 text-emerald-700'}>{rRisk} risk</Badge>
                  </div>
                </div>
              );
            })}</div>
          </CardContent>
        </Card>
      )}

      <Card className="glass-card">
        <CardHeader className="pb-2"><CardTitle className="text-lg text-[#0B1F3A] flex items-center gap-2"><FileText size={18} className="text-[#2272C5]"/>Audit Logs</CardTitle></CardHeader>
        <CardContent>
          {auditLogs.length === 0 ? <p className="text-center py-4 text-slate-500">No logs</p> :
          <div className="space-y-1.5 max-h-[400px] overflow-y-auto">{auditLogs.map(log => {
            const logAction = log.action;
            const logAdmin = log.admin_name;
            const logReason = log.reason;
            const logDate = log.created_at;
            return (
              <div key={log.id} className="p-2 rounded-lg bg-slate-50 text-xs flex items-center gap-2" data-testid={`audit-log-${log.id}`}>
                <Badge className="bg-[#0B1F3A] text-white text-[8px]">{logAction.replace(/_/g,' ')}</Badge>
                <span className="text-slate-600 flex-1 truncate">by {logAdmin} {logReason && `\u2014 ${logReason}`}</span>
                <span className="text-slate-400 flex-shrink-0">{new Date(logDate).toLocaleDateString('en-NG', { month:'short', day:'numeric', hour:'2-digit', minute:'2-digit' })}</span>
              </div>
            );
          })}</div>}
        </CardContent>
      </Card>
    </div>
  );
};
